import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Mail, X, Loader2 } from 'lucide-react';
import { motion, useAnimation } from 'motion/react';
import toast from 'react-hot-toast';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useApproveUser, useRejectUser } from '@/features/users/hooks';
import { useActiveEmploymentRoles } from '@/features/admin/hooks';
import { useHospitals } from '@/features/hospitals';
import { HospitalAssignmentField } from '@/features/users/components/HospitalAssignmentField';
import { localizedName } from '@/utils/localized';
import { triggerHapticFeedback, ADMIN_COLORS } from '../utils/adminAnimations';
import type { PendingUser } from '@/features/users/types';

const jiggle = {
  jiggle: {
    x: [0, -6, 6, -4, 4, -2, 2, 0],
    transition: { duration: 0.4 },
  },
};

interface PendingUserApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: PendingUser;
}

export function PendingUserApprovalModal({
  isOpen,
  onClose,
  user,
}: PendingUserApprovalModalProps) {
  const { t, i18n } = useTranslation();
  const { openConfirm } = useModal();
  const approve = useApproveUser();
  const reject = useRejectUser();
  const { data: employmentRoles } = useActiveEmploymentRoles();
  const { data: hospitalsData } = useHospitals({ isActive: 'true', limit: 100 });
  const hospitals = hospitalsData?.hospitals ?? [];

  const [employmentRoleId, setEmploymentRoleId] = useState('');
  const [hospitalIds, setHospitalIds] = useState<string[]>([]);
  const [primaryHospitalId, setPrimaryHospitalId] = useState<string | undefined>();
  const [shakeField, setShakeField] = useState<'role' | 'primary' | null>(null);

  const roleControls = useAnimation();
  const primaryControls = useAnimation();

  // Auto-select all active hospitals on mount / when hospitals load
  useEffect(() => {
    if (isOpen && hospitals.length > 0) {
      const allIds = hospitals.map((h) => h.id);
      setHospitalIds(allIds);
      if (allIds.length === 1) {
        setPrimaryHospitalId(allIds[0]);
      } else {
        setPrimaryHospitalId(undefined);
      }
    }
  }, [isOpen, hospitals]);

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setEmploymentRoleId('');
      setShakeField(null);
    }
  }, [isOpen]);

  const handleHospitalChange = (ids: string[], newPrimaryId?: string) => {
    setHospitalIds(ids);
    setPrimaryHospitalId(newPrimaryId);
  };

  // Selected hospitals for primary dropdown
  const selectedHospitals = useMemo(
    () => hospitals.filter((h) => hospitalIds.includes(h.id)),
    [hospitals, hospitalIds],
  );

  const handleApprove = useCallback(async () => {
    // Validate and jiggle first missing field
    if (!employmentRoleId) {
      triggerHapticFeedback();
      setShakeField('role');
      roleControls.start('jiggle');
      setTimeout(() => setShakeField(null), 1500);
      return;
    }
    if (!primaryHospitalId) {
      triggerHapticFeedback();
      setShakeField('primary');
      primaryControls.start('jiggle');
      setTimeout(() => setShakeField(null), 1500);
      return;
    }

    triggerHapticFeedback();
    try {
      await approve.mutateAsync({
        id: user.id,
        employmentRoleId,
        hospitalIds,
        primaryHospitalId,
      });
      toast.success(t('userApprovals.approved'));
      onClose();
    } catch {
      toast.error(t('common.error'));
    }
  }, [employmentRoleId, primaryHospitalId, hospitalIds, user.id, approve, onClose, t, roleControls, primaryControls]);

  const handleReject = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('userApprovals.rejectConfirmTitle'),
      message: t('userApprovals.rejectConfirmMessage'),
      variant: 'danger',
      confirmLabel: t('userApprovals.reject'),
      onConfirm: async () => {
        await reject.mutateAsync(user.id);
        toast.success(t('userApprovals.rejected'));
        onClose();
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  const errorBorderStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: '1.5px solid #EF4444',
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('userApprovals.title')}
      isPending={approve.isPending}
      onSubmit={handleApprove}
      isFormValid={true}
      submitLabel={t('userApprovals.approve')}
    >
      <div className="space-y-5">
        {/* User info card */}
        <div
          className="rounded-xl p-4 space-y-2"
          style={{
            backgroundColor: MODAL_COLORS.interactive,
            border: `1px solid ${MODAL_COLORS.border}`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
              style={{
                backgroundColor: ADMIN_COLORS.warningBg,
                color: ADMIN_COLORS.warningText,
                border: `1px solid ${ADMIN_COLORS.border}`,
              }}
            >
              {(user.firstName?.[0] ?? user.email[0]).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-base" style={{ color: MODAL_COLORS.text }}>
                {user.firstName} {user.lastName}
              </p>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Mail className="w-3 h-3" />
                <span className="truncate">{user.email}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5">
                <Calendar className="w-3 h-3" />
                <span>
                  {t('userApprovals.registeredOn')}{' '}
                  {new Date(user.createdAt).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Employment role */}
        <motion.div variants={jiggle} animate={roleControls}>
          <StepHeader step={1} label={t('userApprovals.step1Label')} done={!!employmentRoleId} />
          <select
            value={employmentRoleId}
            onChange={(e) => {
              setEmploymentRoleId(e.target.value);
              setShakeField(null);
            }}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
            style={shakeField === 'role' ? errorBorderStyle : inputStyle}
          >
            <option value="">{t('userApprovals.selectEmploymentRole')}</option>
            {(employmentRoles || []).map((r) => (
              <option key={r.id} value={r.id}>
                {localizedName(r, i18n.language)} ({r.code})
              </option>
            ))}
          </select>
          {shakeField === 'role' && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs font-medium mt-1"
              style={{ color: '#EF4444' }}
            >
              {t('userApprovals.employmentRoleRequired')}
            </motion.p>
          )}
        </motion.div>

        {/* Step 2: Primary hospital */}
        <motion.div variants={jiggle} animate={primaryControls}>
          <StepHeader step={2} label={t('userApprovals.step2Label')} done={!!primaryHospitalId} />
          <select
            value={primaryHospitalId ?? ''}
            onChange={(e) => {
              const val = e.target.value || undefined;
              setPrimaryHospitalId(val);
              // If chosen hospital isn't in the list, add it
              if (val && !hospitalIds.includes(val)) {
                setHospitalIds((prev) => [...prev, val]);
              }
              setShakeField(null);
            }}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
            style={shakeField === 'primary' ? errorBorderStyle : inputStyle}
          >
            <option value="">{t('userApprovals.selectPrimaryHospital')}</option>
            {selectedHospitals.map((h) => (
              <option key={h.id} value={h.id}>
                {localizedName(h, i18n.language)}
                {h.city ? ` — ${h.city}` : ''}
              </option>
            ))}
          </select>
          {shakeField === 'primary' && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs font-medium mt-1"
              style={{ color: '#EF4444' }}
            >
              {t('userApprovals.primaryHospitalRequired')}
            </motion.p>
          )}
        </motion.div>

        {/* Step 3: Hospital list */}
        <div>
          <StepHeader step={3} label={t('userApprovals.step3Label')} done={hospitalIds.length > 0} />
          <p className="text-xs text-gray-400 mb-1.5">
            {t('userApprovals.allHospitalsAutoAdded')}
          </p>
          <HospitalAssignmentField
            selectedIds={hospitalIds}
            primaryId={primaryHospitalId}
            onChange={handleHospitalChange}
          />
        </div>

        {/* Reject button */}
        <button
          type="button"
          onClick={handleReject}
          disabled={reject.isPending}
          className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
          style={{
            backgroundColor: '#FEE2E2',
            color: '#B91C1C',
            border: `1px solid ${MODAL_COLORS.border}`,
          }}
        >
          {reject.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <X className="w-4 h-4" />
          )}
          {t('userApprovals.reject')}
        </button>
      </div>
    </FormModal>
  );
}

function StepHeader({ step, label, done }: { step: number; label: string; done: boolean }) {
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{
          backgroundColor: done ? ADMIN_COLORS.successBg : ADMIN_COLORS.warningBg,
          color: done ? ADMIN_COLORS.successText : ADMIN_COLORS.warningText,
          border: `1px solid ${ADMIN_COLORS.border}`,
        }}
      >
        {step}
      </span>
      <span className="text-sm font-semibold" style={{ color: MODAL_COLORS.text }}>
        {label}
      </span>
    </div>
  );
}
